import React, { useCallback, useMemo, useContext } from 'react'
import OptionsMenu from '../../Common/OptionsMenu/OptionsMenu'
import SectionFormContainer from '../components/SectionFormContainer'
import BoardingWizardContext from './BoardingWizardContext'
import useBoardingVisit from '../../../hooks/useBoardingVisit'
import useDaycareVisit from '../../../hooks/useDaycareVisit'
import useDayRestrictions from '../../../hooks/useDayRestrictions'
import { BOARDING_FIELDS } from './config'
import moment from 'moment'
import StyledAlert from '../../Common/StyledAlert'

const Pickup = (props) => {
	const {
		wizardContextProvider: { wizard_state, setWizardAttributeAndValues }
	} = useContext(BoardingWizardContext)
	const { title = '', subtext = '' } = props
	const { loading, standardBoardingPickupHours } = useBoardingVisit()
	const { daycare } = useDaycareVisit()
	const { getDateRestrictions } = useDayRestrictions()

	const lastDay = useMemo(() => {
		const { dates } = wizard_state
		const lastDay = dates[dates.length - 1]
		return lastDay || null
	}, [wizard_state])

	const daycareNotAvailableOnDate = useMemo(() => {
		if (!lastDay) {
			return false
		}

		const hasRestriction = getDateRestrictions({ date: lastDay })
		if (!hasRestriction) {
			return false
		}

		const { daycareAllowed } = hasRestriction
		return !daycareAllowed
	}, [lastDay, getDateRestrictions])

	const handleSelection = useCallback(
		(value = {}) => {
			const newValues = { ...wizard_state, ...value }
			setWizardAttributeAndValues(newValues)
		},
		[wizard_state, setWizardAttributeAndValues]
	)

	const options = useMemo(() => {
		if (!daycare) {
			return []
		}

		const { extended_pickup, last_day_daycare } = wizard_state

		let formattedDate = 'the last day.'
		if (lastDay) {
			formattedDate = moment(lastDay).format('MM/DD')
		}

		const o = [
			{
				title: <div>Yes, with standard pick-up on {formattedDate}</div>,
				description: (
					<div>
						<div>
							I will pick-up by {moment(daycare.standard_pickup_time, 'HH:mm').format('h:mma')}
						</div>
					</div>
				),
				disabled: loading || daycareNotAvailableOnDate,
				selected: last_day_daycare && !extended_pickup,
				onSelected: () => {
					const update = {
						[BOARDING_FIELDS.last_day_daycare]: true,
						[BOARDING_FIELDS.extended_pickup]: false
					}
					handleSelection(update)
				},
				style: { width: '22rem', maxWidth: 'unset' }
			},
			{
				title: <div>Yes, with late pick-up on {formattedDate}</div>,
				description: (
					<div>
						I will pick-up by{' '}
						{moment(daycare.late_pickup_standard_pickup_time, 'HH:mm').format('h:mma')}
					</div>
				),
				disabled: loading || daycareNotAvailableOnDate,
				selected: last_day_daycare && extended_pickup,
				onSelected: () => {
					const update = {
						[BOARDING_FIELDS.last_day_daycare]: true,
						[BOARDING_FIELDS.extended_pickup]: true
					}
					handleSelection(update)
				},
				style: { width: '22rem', maxWidth: 'unset' }
			},
			{
				title: `No, I don't need daycare on ${formattedDate}`,
				description: `I will pick-up ${standardBoardingPickupHours}`,
				disabled: loading || !last_day_daycare,
				selected: !last_day_daycare,
				onSelected: () => {
					const update = {
						[BOARDING_FIELDS.last_day_daycare]: false,
						[BOARDING_FIELDS.extended_pickup]: false
					}
					handleSelection(update)
				},
				style: { width: '22rem', maxWidth: 'unset' }
			}
		]

		return o
	}, [
		handleSelection,
		loading,
		wizard_state,
		daycare,
		lastDay,
		standardBoardingPickupHours
	])

	return (
		<>
			<SectionFormContainer title={title} subtext={subtext}>
				<OptionsMenu options={options} />
				{daycareNotAvailableOnDate ? (
					<StyledAlert
						style={{ marginTop: '.8rem' }}
						type="warning"
						text="Daycare not available on this date"
					/>
				) : null}
			</SectionFormContainer>
		</>
	)
}

export default Pickup
